<script setup lang="ts">
import { onMounted, provide, ref } from 'vue'

import { storeToRefs } from 'pinia'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'
import OpportunityList from '@/components/features/opportunities/OpportunityList.vue'
import OpportunityCreate from '@/components/features/opportunities/OpportunityCreate.vue'
import OpportunityFilters from '@/components/features/opportunities/OpportunityFilters.vue'
import OpportunityKanban from '@/components/features/opportunities/OpportunityKanban.vue'
import { useUserStore } from '@/stores/useUserStore'
import { ViewColumnsIcon, TableCellsIcon } from '@heroicons/vue/24/outline'
import type { Context } from '@/utils/types'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'

const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const opportunityStore = useOpportunityStore()
const { opportunities, filteredOpportunities, loadingOpportunities } = storeToRefs(opportunityStore)
const { userConnected } = storeToRefs(userStore)
const kanbanView = ref(true)

const context: Context = 'user'

provide('context', context)

onMounted(async () => {
  if (boostCoreStore.user?.email && !userConnected.value) {
    await userStore.getUserByEmail(boostCoreStore.user?.email)
  }

  await userStore.getAllUsers()
  await opportunityStore.loadOpportunities()
})
</script>

<template>
  <div class="flex flex-col w-full h-screen overflow-hidden">
    <div class="p-8 pb-0">
      <div class="flex flex-col gap-12 mb-2">
        <Bs2Title title="Affaires" />
      </div>
      <OpportunityCreate />
      <div class="flex flex-nowrap gap-4 mt-2 items-center justify-start md:justify-center">
        <OpportunityFilters :opportunities="opportunities || []" />
        <div class="self-start mt-6">
          <label class="swap swap-flip">
            <input type="checkbox" v-model="kanbanView" />
            <div class="swap-on tooltip tooltip-left" data-tip="Liste">
              <component :is="TableCellsIcon" class="w-6 h-6" />
            </div>
            <div class="swap-off tooltip tooltip-left" data-tip="Kanban">
              <component :is="ViewColumnsIcon" class="w-6 h-6" />
            </div>
          </label>
        </div>
      </div>

      <div v-if="loadingOpportunities" class="w-full flex justify-center py-4">
        <LoaderCenter />
      </div>
    </div>

    <div class="flex-1 overflow-auto px-8">
      <OpportunityKanban v-if="kanbanView" :data="filteredOpportunities" />
      <OpportunityList
        v-else-if="!kanbanView"
        :data="filteredOpportunities"
        :showAddButton="false"
        :filter="false"
      />
    </div>
  </div>
</template>
